"use client";

// SNOWCLUB-V101-GA4-DESIGN-SEARCH-ITEM-20261002-002 (correction) — the GA4 consumer for the booking results route.
//
// `/…/booking/schedule/<leg>/<route>` renders the DESIGN (ExportShell), not `BookingResults`, so the search handoff and
// view_item_list watchers that lived in `BookingResults` never mounted in production. This is the mounted consumer: it
// sits beside the design surface, renders nothing, and
//   - on the depart board consumes the landing Search's one-shot handoff (read, delete, send) once the tag is ready;
//   - receives the design's own post-filter, post-loading `visRides()` signal and reports that board as ONE
//     view_item_list per distinct displayed board. Server-side `hostedRides` is deliberately not used: it is
//     the source list, not necessarily the list the customer can see after filters.
// Both go through the existing gated helpers, so the production-host + initialized-tag gates, the handoff TTL, the non-PII
// slug term and the de-duplication are unchanged.

import { useEffect, useRef } from "react";
import { gaItemsFromDisplayedRides, watchSearchHandoff, watchViewItemList } from "@/lib/analytics-design-search";
import type { DisplayedRide } from "@/lib/analytics-design-search";

interface DisplayedListSignal { type?: unknown; event?: unknown; leg?: unknown; member?: unknown; rides?: unknown }
interface ResultsWaitingSignal { leg?: unknown }

const signalRides = (value: unknown): DisplayedRide[] => (Array.isArray(value) ? value.filter((ride): ride is DisplayedRide => !!ride && typeof ride === "object") : []);

export default function FunnelAnalyticsBridge({ leg, routeSlug }: { leg: "depart" | "return"; routeSlug: string }) {
  const activeList = useRef<{ fingerprint: string; off: () => void } | null>(null);

  useEffect(() => (leg === "depart" ? watchSearchHandoff(routeSlug) : undefined), [leg, routeSlug]);

  const listId = `transport_${leg}_${routeSlug}`;
  useEffect(() => {
    const announceReady = () => window.dispatchEvent(new CustomEvent("sg:ga-results-ready", { detail: { leg } }));
    const onResultsWaiting = (event: Event) => {
      if ((event as CustomEvent<ResultsWaitingSignal>).detail?.leg === leg) announceReady();
    };
    const onDisplayedList = (event: Event) => {
      const signal = (event as CustomEvent<DisplayedListSignal>).detail;
      if (signal?.type !== "sg:ga" || signal.event !== "view_item_list" || signal.leg !== leg) return;
      const items = gaItemsFromDisplayedRides(signalRides(signal.rides), leg, signal.member === true);
      const fingerprint = `${leg}|${signal.member === true}|${items.map((item) => `${item.item_id}@${item.price}`).join(",")}`;
      if (activeList.current?.fingerprint === fingerprint) return;
      // A changed filter/loading state replaces the pending board. Otherwise an old, no-longer-visible
      // list could be emitted later when consent or the tag becomes ready.
      activeList.current?.off();
      activeList.current = items.length
        ? { fingerprint, off: watchViewItemList(listId, `Transport ${leg} results`, items) }
        : { fingerprint, off: () => undefined };
    };
    window.addEventListener("sg:ga", onDisplayedList);
    window.addEventListener("sg:ga-results-waiting", onResultsWaiting);
    // Covers the usual order (the design class mounted before this sibling useEffect).
    // The waiting listener above covers the inverse order and remount/replay cases.
    announceReady();
    return () => {
      window.removeEventListener("sg:ga", onDisplayedList);
      window.removeEventListener("sg:ga-results-waiting", onResultsWaiting);
      activeList.current?.off();
      activeList.current = null;
    };
  }, [leg, listId]);

  return null;
}
